/**
 * ストーリー KV
 *
 * ストーリーのキービジュアルを表示するコンポーネントです。
 *
 * ist-js__storyKeyVisual のクラスを持っている場合、ist-js__storyKeyVisualの左側からbodyの右端（スクロールバー）までの距離を取得して、
 * その幅を.ist-js__storyKeyVisualの幅にします。
 * リサイズが発生した場合、再計算する。
 *
 * 960px以下の場合は、幅を100%に設定します。
 */
class StoryKV {
  private readonly selector: string;
  private resizeListeners: Map<HTMLElement, () => void> = new Map();

  /**
   * コンストラクター
   * @param elementSelector - ストーリー KV 要素のセレクター
   */
  constructor(elementSelector: string = '.ist-js__storyKeyVisual') {
    this.selector = elementSelector;
    this.init();
  }

  /**
   * 初期化
   */
  private init(): void {
    const elements = document.querySelectorAll<HTMLElement>(this.selector);
    if (!elements.length) {
      console.warn('ストーリー KV 要素が見つかりません');
      return;
    }

    elements.forEach((element) => {
      // 初回の幅調整
      this.adjustWidth(element);

      /**
       * デバウンス関数
       * @param func - 実行する関数
       * @param wait - 待機時間（ミリ秒）
       * @returns デバウンスされた関数
       */
      const debounce = (func: Function, wait: number) => {
        let timeout: number | undefined;
        return () => {
          clearTimeout(timeout);
          timeout = window.setTimeout(() => {
            func();
          }, wait);
        };
      };

      // デバウンスされた幅再計算関数を作成
      const debouncedAdjustWidth = debounce(() => {
        this.adjustWidth(element);
      }, 100);

      // リサイズ時に幅を再計算
      window.addEventListener('resize', debouncedAdjustWidth);

      // クリーンアップのためにリスナーを保存（必要に応じて破棄処理で利用可能）
      this.resizeListeners.set(element, debouncedAdjustWidth);
    });
  }

  /**
   * ストーリー KV 要素の幅を調整します。
   * 960px以下の場合は100%、それ以上の場合は要素の左側からbodyの右端までの距離を計算して幅を設定します。
   */
  private adjustWidth(element: HTMLElement): void {
    // 960px以下は幅を100%に設定
    if (window.innerWidth <= 960) {
      element.style.width = '100%';
      return;
    }

    // body のクライアント幅を取得
    const bodyWidth = document.body.clientWidth;

    // 要素の左端の位置を取得（ページ全体の左端からの距離）
    const rect = element.getBoundingClientRect();
    const elementLeft = rect.left + window.scrollX;

    // body の右端までの距離を計算
    const distanceToRight = bodyWidth - elementLeft;

    // 計算した距離を要素の幅として設定
    element.style.width = `${distanceToRight}px`;
  }
}

export default StoryKV;
